import { CustomerResource } from 'types/apiTypes';
import { VideoScene, VideoProposal } from './types';

let idCounter = 0;
const nextId = () => `scene-${++idCounter}-${Date.now()}`;

const FALLBACK_IMAGE = 'https://picsum.photos/seed/fallback/400/400';

const DEFAULT_HEADLINES = [
  "We're Hiring!",
  'Your Next Career Move',
  'Great Benefits & Culture',
  'Apply Now',
];

const DEFAULT_SUBTEXTS = [
  'Join a team that makes a difference',
  'Grow with us in a dynamic environment',
  'Flexible work, competitive salary, amazing team',
  "Don't miss this opportunity",
];

/**
 * Create mock scenes using real customer images when available.
 * Falls back to placeholder images if not enough brand creatives exist.
 */
export const createMockScenes = (
  customerImages?: CustomerResource[],
  vacancyTitle?: string,
  companyName?: string
): VideoScene[] => {
  const images = customerImages ?? [];
  const headlines = [...DEFAULT_HEADLINES];

  // Personalize headlines if vacancy/company info is available
  if (vacancyTitle) {
    headlines[0] = vacancyTitle;
  }
  if (companyName) {
    headlines[1] = `Join ${companyName}`;
  }

  return headlines.map((headline, i) => ({
    id: nextId(),
    index: i,
    headline,
    subtext: DEFAULT_SUBTEXTS[i] || '',
    imageUrl: images[i]?.path ?? `https://picsum.photos/seed/scene${i}/400/400`,
    duration: i === headlines.length - 1 ? 4 : 3,
    transition: (['fade', 'dissolve', 'slide', 'fade'] as const)[i] || 'fade',
  }));
};

export const createMockProposal = (
  customerImages?: CustomerResource[],
  vacancyTitle?: string,
  companyName?: string
): VideoProposal => {
  const scenes = createMockScenes(customerImages, vacancyTitle, companyName);
  return {
    scenes,
    templateStyle: 'dynamic',
    totalDuration: scenes.reduce((sum, s) => sum + s.duration, 0),
  };
};

/**
 * The three looks, in the order the style step draws them.
 *
 * SYM-6226, 22 September 2026. Each entry used to carry two pictures of its
 * own: `clipUrl`, a clip shipped in `public/video-styles/`, and `previewUrl`, a
 * stock photograph standing in behind it. Both are gone with the files
 * themselves. What a look does to THIS customer's video is now shown by a
 * moving example the server draws from the draft's own picture, colour and
 * logo, which is a thing no file shipped in the app can be.
 *
 * What is left here is what a picture cannot say: the look's name and what it
 * does, in words, and its colour for the bar under the example. Those three
 * lines are also the whole step when the examples cannot be loaded.
 */
export const TEMPLATE_STYLES = [
  {
    id: 'dynamic' as const,
    name: 'Dynamic',
    description:
      'Bold heavy text, your brand colour washed across the picture, and a slow drift across each photo',
    // SYM-6353. A Bring to Life clip already moves, so there Dynamic draws the
    // text and the wash and no drift, and says so.
    onAClip: 'Bold heavy text, your brand colour washed across the picture',
    color: '#fd4c5a',
  },
  {
    id: 'professional' as const,
    name: 'Professional',
    description: 'Clear bold text, each line on a bar of your brand colour',
    onAClip: 'Clear bold text, each line on a bar of your brand colour',
    color: '#6F6DEC',
  },
  {
    id: 'minimal' as const,
    name: 'Minimal',
    description: 'Light, quiet text straight on the photo, no brand colour',
    onAClip: 'Light, quiet text straight on the photo, no brand colour',
    color: '#192F58',
  },
];

export const TRANSITION_OPTIONS = [
  { value: 'fade' as const, label: 'Fade' },
  { value: 'dissolve' as const, label: 'Dissolve' },
  { value: 'slide' as const, label: 'Slide' },
  { value: 'cut' as const, label: 'Cut' },
];
